const NICE_STEP_MULTIPLIERS = [1, 2, 5, 10]

/**
 * Evenly spaced round ticks covering [min, max]. The step is 1, 2 or 5
 * times a power of ten, aiming for about `targetCount` ticks.
 */
export function getNiceAxisTicks(
  min: number,
  max: number,
  targetCount = 5,
): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return [min, min + 1]
  }
  const rawStep = (max - min) / Math.max(targetCount - 1, 1)
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const step =
    (NICE_STEP_MULTIPLIERS.find(
      (multiplier) => multiplier * magnitude >= rawStep * (1 - 1e-9),
    ) ?? 10) * magnitude
  const first = Math.floor(min / step + 1e-9)
  const last = Math.ceil(max / step - 1e-9)
  const ticks: number[] = []
  for (let index = first; index <= last; index += 1) {
    ticks.push(Number((index * step).toPrecision(12)))
  }
  return ticks
}

/** Smallest and largest numeric value of `keys` across `rows`, or null when there are none. */
export function getSeriesExtent(
  rows: ReadonlyArray<Record<string, unknown>>,
  keys: readonly string[],
): [number, number] | null {
  let low = Infinity
  let high = -Infinity
  for (const row of rows) {
    for (const key of keys) {
      const value = row[key]
      if (typeof value === "number" && Number.isFinite(value)) {
        low = Math.min(low, value)
        high = Math.max(high, value)
      }
    }
  }
  return low <= high ? [low, high] : null
}

/**
 * Line chart y-axis ticks: the data extent widened to zero, the metric's
 * upper bound and any reference areas. `upperFloor` is undefined when the
 * chart has no bound. Undefined when there is nothing to scale to.
 */
export function getLineChartAxisTicks({
  extent,
  upperFloor,
  referenceValues,
}: {
  extent: [number, number] | null
  upperFloor: number | undefined
  referenceValues: readonly number[]
}): number[] | undefined {
  if (!extent && upperFloor === undefined && referenceValues.length === 0) {
    return undefined
  }
  return getNiceAxisTicks(
    Math.min(extent?.[0] ?? 0, 0, ...referenceValues),
    Math.max(extent?.[1] ?? 0, upperFloor ?? 0, ...referenceValues),
  )
}

/**
 * Largest stack total across `rows`. Each entry of `stacks` lists the keys
 * drawn on top of one another; a key drawn alone is a stack of one. Null when
 * no stack has a numeric value.
 */
export function getStackedSeriesMax(
  rows: ReadonlyArray<Record<string, unknown>>,
  stacks: ReadonlyArray<readonly string[]>,
): number | null {
  let high: number | null = null
  for (const row of rows) {
    for (const stack of stacks) {
      let total = 0
      let hasValue = false
      for (const key of stack) {
        const value = row[key]
        if (typeof value === "number" && Number.isFinite(value)) {
          total += value
          hasValue = true
        }
      }
      if (hasValue && (high === null || total > high)) {
        high = total
      }
    }
  }
  return high
}

/**
 * Success-rate axis ticks from zero up to the largest drawn value or
 * reference line, so low rates fill the chart instead of hugging the floor of
 * a 0–100% axis. A chart with no positive value keeps the full 0–100% range.
 */
export function getPercentAxisTicks(
  dataMax: number | null,
  referenceValues: readonly number[] = [],
): number[] {
  const max = Math.max(dataMax ?? 0, ...referenceValues)
  return getNiceAxisTicks(0, max > 0 ? max : 1)
}
